import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrophyIcon, 
  StarIcon,
  FireIcon,
  SparklesIcon,
  GlobeAltIcon,
  CameraIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Postcard, Achievement } from '../types';
import { Timestamp } from 'firebase/firestore';

// Helper function to convert Firestore timestamp to Date
const convertToDate = (timestamp: Timestamp | Date | null | undefined): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if ('seconds' in timestamp && timestamp.seconds) return new Date(timestamp.seconds * 1000);
  if ('toDate' in timestamp && timestamp.toDate) return timestamp.toDate();
  return new Date(String(timestamp));
};

export function AchievementsPage() {
  const { currentUser } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchUserData();
  }, [currentUser]);

  const fetchUserData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Fetch user's postcards
      const postcardsQuery = query(
        collection(db, 'postcards'),
        where('createdBy', '==', currentUser.uid)
      );
      const postcardsSnapshot = await getDocs(postcardsQuery);
      const postcardsData: Postcard[] = [];
      
      postcardsSnapshot.forEach((doc) => {
        const data = doc.data();
        postcardsData.push({
          id: doc.id,
          ...data,
          createdAt: convertToDate(data.createdAt) || new Date()
        } as Postcard);
      });
      
      // Generate achievements based on user activity
      const allAchievements: Achievement[] = [
        {
          id: 'first-postcard',
          title: 'First Postcard',
          description: 'Send your first postcard',
          icon: '📮',
          points: 10,
          category: 'milestone' as const,
          unlockedAt: postcardsData.length > 0 ? new Date() : undefined,
          requirements: {
            postcardsSent: 1
          }
        },
        {
          id: '10-postcards',
          title: 'Postcard Enthusiast',
          description: 'Send 10 postcards',
          icon: '📬',
          points: 50,
          category: 'milestone' as const,
          unlockedAt: postcardsData.length >= 10 ? new Date() : undefined,
          requirements: {
            postcardsSent: 10
          }
        },
        {
          id: '50-postcards',
          title: 'Postcard Master',
          description: 'Send 50 postcards',
          icon: '📭',
          points: 200,
          category: 'milestone' as const,
          unlockedAt: postcardsData.length >= 50 ? new Date() : undefined,
          requirements: {
            postcardsSent: 50
          }
        },
        {
          id: '100-postcards',
          title: 'Postcard Legend',
          description: 'Send 100 postcards',
          icon: '📪',
          points: 500,
          category: 'milestone' as const,
          unlockedAt: postcardsData.length >= 100 ? new Date() : undefined,
          requirements: {
            postcardsSent: 100
          }
        },
        {
          id: '3-countries',
          title: 'Explorer',
          description: 'Send postcards from 3 different countries',
          icon: '🗺️',
          points: 100,
          category: 'exploration' as const,
          unlockedAt: new Set(postcardsData.map(p => p.country)).size >= 3 ? new Date() : undefined,
          requirements: {
            countriesReached: 3
          }
        },
        {
          id: '5-countries',
          title: 'World Traveler',
          description: 'Send postcards from 5 different countries',
          icon: '🌍',
          points: 250,
          category: 'exploration' as const,
          unlockedAt: new Set(postcardsData.map(p => p.country)).size >= 5 ? new Date() : undefined,
          requirements: {
            countriesReached: 5
          }
        },
        {
          id: 'all-continents',
          title: 'Global Explorer',
          description: 'Send postcards from all continents',
          icon: '🌎',
          points: 1000,
          category: 'exploration' as const,
          unlockedAt: new Set(postcardsData.map(p => p.country)).size >= 7 ? new Date() : undefined,
          requirements: {
            countriesReached: 7
          }
        },
        {
          id: 'first-like',
          title: 'Liked',
          description: 'Receive your first like',
          icon: '❤️',
          points: 25,
          category: 'social' as const,
          unlockedAt: undefined,
          requirements: {
            postcardsReceived: 1
          }
        },
        {
          id: '10-likes',
          title: 'Popular',
          description: 'Receive 10 likes',
          icon: '💖',
          points: 100,
          category: 'social' as const,
          unlockedAt: undefined,
          requirements: {
            postcardsReceived: 10
          }
        },
        {
          id: '50-likes',
          title: 'Viral',
          description: 'Receive 50 likes',
          icon: '💝',
          points: 500,
          category: 'social' as const,
          unlockedAt: undefined,
          requirements: {
            postcardsReceived: 50
          }
        },
        {
          id: 'social-butterfly',
          title: 'Social Butterfly',
          description: 'Have a high reply rate',
          icon: '🦋',
          points: 300,
          category: 'social' as const,
          unlockedAt: undefined,
          requirements: {
            replyRate: 80
          }
        },
        {
          id: 'photographer',
          title: 'Photographer',
          description: 'Send 25 postcards with great photos',
          icon: '📸',
          points: 400,
          category: 'creative' as const,
          unlockedAt: postcardsData.length >= 25 ? new Date() : undefined,
          requirements: {
            postcardsSent: 25
          }
        },
        {
          id: 'creative-genius',
          title: 'Creative Genius',
          description: 'Use all available templates and features',
          icon: '🎨',
          points: 750,
          category: 'creative' as const,
          unlockedAt: undefined,
          requirements: {
            postcardsSent: 50
          }
        }
      ];
      
      setAchievements(allAchievements);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const unlockedAchievements = achievements.filter(a => a.unlockedAt);
  const totalPoints = unlockedAchievements.reduce((sum, a) => sum + a.points, 0);
  const completionRate = Math.round((unlockedAchievements.length / achievements.length) * 100);

  const filteredAchievements = selectedCategory === 'all' 
    ? achievements 
    : achievements.filter(a => a.category === selectedCategory);

  const categories = [
    { id: 'all', name: 'All', icon: TrophyIcon, color: 'text-gray-600' },
    { id: 'milestone', name: 'Milestones', icon: StarIcon, color: 'text-yellow-600' },
    { id: 'exploration', name: 'Exploration', icon: GlobeAltIcon, color: 'text-blue-600' },
    { id: 'social', name: 'Social', icon: UserGroupIcon, color: 'text-green-600' },
    { id: 'creative', name: 'Creative', icon: CameraIcon, color: 'text-purple-600' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold mb-2">Achievements</h1>
          <p className="text-yellow-100">
            Unlock badges and track your progress
          </p>
        </motion.div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-yellow-600">{totalPoints}</div>
          <div className="text-gray-600">Total Points</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{unlockedAchievements.length}</div>
          <div className="text-gray-600">Achievements Unlocked</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{completionRate}%</div>
          <div className="text-gray-600">Completion Rate</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-600">{achievements.length - unlockedAchievements.length}</div>
          <div className="text-gray-600">Remaining</div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center px-4 py-2 rounded-lg border-2 transition-all ${
                selectedCategory === category.id
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <category.icon className={`h-4 w-4 mr-2 ${category.color}`} />
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAchievements.map((achievement) => (
          <motion.div
            key={achievement.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`card transition-all transform hover:scale-105 ${
              achievement.unlockedAt ? 'ring-2 ring-yellow-400' : ''
            }`}
          >
            <div className="text-center">
              <div className={`text-4xl mb-4 ${achievement.unlockedAt ? '' : 'grayscale opacity-50'}`}>
                {achievement.icon}
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {achievement.title}
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                {achievement.description}
              </p>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">
                  {achievement.points} points
                </span>
                
                {achievement.unlockedAt ? (
                  <div className="flex items-center text-green-600">
                    <StarIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">Unlocked</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-400">
                    <span className="text-sm">Locked</span>
                  </div>
                )}
              </div>
              
                             {achievement.unlockedAt && (
                 <div className="mt-3 text-xs text-gray-500">
                   Unlocked {(() => {
                     const date = convertToDate(achievement.unlockedAt);
                     return date ? date.toLocaleDateString() : 'Unknown';
                   })()}
                 </div>
               )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <SparklesIcon className="h-5 w-5 mr-2 text-yellow-600" />
          Overall Progress
        </h2>
        
        <div className="space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Achievement Progress</span>
            <span className="font-medium">{unlockedAchievements.length} / {achievements.length}</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-yellow-500 to-orange-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${completionRate}%` }}
            ></div>
          </div>
          
          <div className="text-center">
            <span className="text-sm text-gray-600">
              {completionRate}% Complete
            </span>
          </div>
        </div>
      </div>

      {/* Next Achievements */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <FireIcon className="h-5 w-5 mr-2 text-orange-600" />
          Next Achievements to Unlock
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements
            .filter(a => !a.unlockedAt)
            .slice(0, 4)
            .map((achievement) => (
              <div key={achievement.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3 opacity-50">{achievement.icon}</span>
                <div>
                  <h4 className="font-medium text-gray-900">{achievement.title}</h4>
                  <p className="text-sm text-gray-600">{achievement.description}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
} 